import { EntityImage, Images } from '@urbanx/agx-ui-components';
import {
  AgencyModel,
  useGetAgenciesAssignedToStaffMember,
  useGetAllAgencies,
} from '@urbanx/agx.agencies.client';
import { AuthContext, StaffRole } from '@urbanx/agx.auth.client';
import { SelectChipOption, useToast } from '@urbanx/agx.ui-components2';
import AppConfig from 'config/AppConfig';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export const MY_AGENCIES_PRESET = 'preset-my-agencies';

type UseAgencyFilterArgs = {
  selectedValues?: string[];
};

type UseAgencyFilterResult = {
  agencyOptions: SelectChipOption[];
  effectiveAgencyIds?: string[];
  agencies: AgencyModel[];
  isPending: boolean;
  error?: Error;
  isAgencyScopeUnavailable: boolean;
  hasNoMatchingAgencies: boolean;
};

const agenciesLoadError = new Error('Failed to load agencies');

export function useAgencyFilter({ selectedValues }: UseAgencyFilterArgs): UseAgencyFilterResult {
  const { userAccount } = useContext(AuthContext);
  const { pushToast } = useToast();
  const currentUserId = AppConfig.userIdClaim ?? userAccount.agent;
  const includesMyAgencies = selectedValues?.includes(MY_AGENCIES_PRESET) ?? false;
  const explicitlySelectedAgencyIds = useMemo(
    () => selectedValues?.filter((value) => value !== MY_AGENCIES_PRESET) ?? [],
    [selectedValues]
  );
  const [assignedAgencyIds, setAssignedAgencyIds] = useState<string[]>([]);
  const [assignmentError, setAssignmentError] = useState<Error>();
  const [hasResolvedAssignments, setHasResolvedAssignments] = useState(false);
  const requestedUserId = useRef<string | undefined>(undefined);
  const {
    data: agencies,
    isPending: isAgenciesPending,
    isError: isAgenciesError,
  } = useGetAllAgencies();

  const handleAssignmentsLoaded = useCallback((agencyIds?: string[]) => {
    setAssignedAgencyIds(agencyIds ?? []);
    setAssignmentError(undefined);
    setHasResolvedAssignments(true);
  }, []);

  const handleAssignmentsError = useCallback((error: Error) => {
    setAssignmentError(error);
    setHasResolvedAssignments(true);
  }, []);

  const {
    getAgenciesAssignedToStaffMember,
    getAgenciesAssignedToStaffMemberPending: isAssignmentsPending,
  } = useGetAgenciesAssignedToStaffMember({
    onSuccess: handleAssignmentsLoaded,
    onError: handleAssignmentsError,
  });

  useEffect(() => {
    if (!currentUserId) {
      setAssignmentError(new Error('Current staff member ID is required'));
      setHasResolvedAssignments(true);
      return;
    }

    if (requestedUserId.current === currentUserId) return;

    requestedUserId.current = currentUserId;
    setAssignmentError(undefined);
    setHasResolvedAssignments(false);
    void getAgenciesAssignedToStaffMember({
      staffMemberId: currentUserId,
      roles: [StaffRole.AdminManager],
    });
  }, [currentUserId, getAgenciesAssignedToStaffMember]);

  const agencyOptions = useMemo<SelectChipOption[]>(
    () => [
      {
        label: 'My Agencies',
        value: MY_AGENCIES_PRESET,
        selected: includesMyAgencies,
        icon: <Images.PeopleOutline />,
      },
      ...(agencies ?? []).map((agency) => mapAgencyToSelectOption(agency, selectedValues)),
    ],
    [agencies, includesMyAgencies, selectedValues]
  );

  const effectiveAgencyIds = useMemo(() => {
    if (includesMyAgencies && (assignmentError || !hasResolvedAssignments)) return undefined;

    const agencyIds = includesMyAgencies
      ? [...new Set([...assignedAgencyIds, ...explicitlySelectedAgencyIds])]
      : explicitlySelectedAgencyIds;

    return agencyIds.length > 0 ? agencyIds : includesMyAgencies ? [] : undefined;
  }, [
    assignedAgencyIds,
    assignmentError,
    explicitlySelectedAgencyIds,
    hasResolvedAssignments,
    includesMyAgencies,
  ]);

  const isPending =
    isAgenciesPending || (includesMyAgencies && (!hasResolvedAssignments || isAssignmentsPending));
  const error =
    (includesMyAgencies ? assignmentError : undefined) ??
    (isAgenciesError ? agenciesLoadError : undefined);

  useEffect(() => {
    if (!error) return;

    pushToast({
      message: 'Unable to load agency filters',
      type: 'error',
    });
  }, [error, pushToast]);

  return {
    agencyOptions,
    effectiveAgencyIds,
    agencies: agencies ?? [],
    isPending,
    error,
    isAgencyScopeUnavailable: includesMyAgencies && !!assignmentError,
    hasNoMatchingAgencies:
      includesMyAgencies &&
      hasResolvedAssignments &&
      !assignmentError &&
      effectiveAgencyIds?.length === 0,
  };
}

export function mapAgencyToSelectOption(
  agency: AgencyModel,
  selectedValues?: string[]
): SelectChipOption {
  return {
    value: agency.id!,
    label: agency.name!,
    selected: selectedValues?.includes(agency.id ?? '') ?? false,
    icon: (
      <EntityImage addRingToImage={true} size={32} photoUri={agency.logoUrl} name={agency.name} />
    ),
  };
}
